import { RotateCcw, Search, SlidersHorizontal, X } from "lucide-react";
import { DEFAULT_FILTERS, type JobFilters } from "../lib/jobs";
import type { WorkType } from "../types/job";

interface FilterPanelProps {
  filters: JobFilters;
  regions: string[];
  open: boolean;
  onChange: (filters: JobFilters) => void;
  onClose: () => void;
}
const WORK_TYPES: Array<{ value: WorkType; label: string }> = [
  { value: "remote", label: "Remote" },
  { value: "hybrid", label: "Hybrid" },
  { value: "onsite", label: "On-site" },
];

const AGE_OPTIONS: Array<{ value: JobFilters["maxAgeHours"]; label: string }> = [
  { value: 24, label: "24h" },
  { value: 168, label: "7d" },
  { value: 240, label: "10d" },
  { value: 336, label: "14d" },
  { value: 720, label: "30d" },
];

export function FilterPanel({
  filters,
  regions,
  open,
  onChange,
  onClose,
}: FilterPanelProps) {
  const update = <Key extends keyof JobFilters>(key: Key, value: JobFilters[Key]) => {
    onChange({ ...filters, [key]: value });
  };

  const toggleWorkType = (workType: WorkType) => {
    const next = filters.workTypes.includes(workType)
      ? filters.workTypes.filter((value) => value !== workType)
      : [...filters.workTypes, workType];
    update("workTypes", next);
  };

  return (
    <aside className={`filter-panel ${open ? "is-open" : ""}`} aria-label="Job filters">
      <div className="panel-heading">
        <div>
          <span className="eyebrow"><SlidersHorizontal size={12} /> Signal filter</span>
          <h2>Search field</h2>
        </div>
        <button className="icon-button panel-close" type="button" onClick={onClose} aria-label="Close filters">
          <X size={17} />
        </button>
      </div>

      <label className="search-field">
        <span className="sr-only">Search by title, company, or keyword</span>
        <Search size={15} />
        <input
          type="search"
          value={filters.search}
          onChange={(event) => update("search", event.target.value)}
          placeholder="Title, company, keyword"
        />
      </label>

      <fieldset className="filter-group">
        <legend>Work type</legend>
        <div className="toggle-row">
          {WORK_TYPES.map((type) => (
            <button
              key={type.value}
              type="button"
              className={filters.workTypes.includes(type.value) ? "is-active" : ""}
              aria-pressed={filters.workTypes.includes(type.value)}
              onClick={() => toggleWorkType(type.value)}
            >
              {type.label}
            </button>
          ))}
        </div>
      </fieldset>

      <div className="filter-group filter-pair">
        <label htmlFor="region">Country / region</label>
        <select id="region" value={filters.region} onChange={(event) => update("region", event.target.value)}>
          <option value="all">Worldwide</option>
          {regions.map((region) => <option key={region} value={region}>{region}</option>)}
        </select>
      </div>

      <fieldset className="filter-group">
        <legend>Posted within</legend>
        <div className="toggle-row age-row">
          {AGE_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={filters.maxAgeHours === option.value ? "is-active" : ""}
              aria-pressed={filters.maxAgeHours === option.value}
              onClick={() => update("maxAgeHours", option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </fieldset>

      <div className="filter-group salary-filter">
        <label htmlFor="salary">
          <span>CAD salary target</span>
          <output htmlFor="salary">${Math.round(filters.minSalary / 1000)}K CAD</output>
        </label>
        <input
          id="salary"
          type="range"
          min="70000"
          max="90000"
          step="5000"
          value={filters.minSalary}
          onChange={(event) => update("minSalary", Number(event.target.value))}
        />
        <div className="range-labels"><span>$70K</span><span>$90K</span></div>
        <p className="salary-note">Undisclosed roles stay visible for review. Verified international pay is converted to CAD.</p>
      </div>

      <button className="reset-button" type="button" onClick={() => onChange(DEFAULT_FILTERS)}>
        <RotateCcw size={13} /> Reset field
      </button>
    </aside>
  );
}
